import { useCallback } from 'react';
import {
  DataVizType,
  DynamicTitle,
  EventName,
  FilterSections,
  Filters,
  Params,
  SelectionMenu,
  SettingDefinition,
  SettingsValues,
  TitleSelector,
  SettingsProvider,
  Context,
  ListMenuItem,
  ListBoxMenuItem,
} from '../types';
import { ContextManager } from '../context/manager';
import { EventParams } from '../types/analytics/index';
import { DataStore } from '../data/store';
import { getChosenCategories, getLabelLookups } from '../filters/filterTracking.hook';
import { createLowerCamelCase } from '../common/util';
import { mapDataVizForElementType, mapDataVizForChartType } from './utils';
import { ChosenCategoryType, DataAvailability, EventParamType } from './types';
import { useAnalyticsContext } from './analytics.provider';

export const getTopSelectors = (settingsValues: SettingsValues, definitions: SettingDefinition[]) => {
  return Object.keys(settingsValues).reduce((acc, curr) => {
    const definition = definitions.find(({ name }) => name === curr);
    // track property if the tracking property name is defined
    const propertyTrackingName = definition?.metadata?.tracking?.propertyName;

    if (definition && propertyTrackingName) {
      const { includePropertyValue = false } = definition.metadata.tracking;
      const params = {
        ...acc,
        [propertyTrackingName]: settingsValues[curr][0]?.label,
      };
      if (includePropertyValue) {
        params[`${propertyTrackingName}Values`] = settingsValues[curr][0]?.value;
      }
      return params;
    }
    return acc;
  }, {});
};

export const getAppliedFilters = (filters: Filters | undefined) => {
  if (!filters) {
    return { globalFilterApplied: 'No' };
  }
  const { sections, values } = filters;
  if (!Object.keys(values).length || !sections || sections.length === 0) {
    return { globalFilterApplied: 'No' };
  }
  const marketFilters = sections?.find(section => section.label === 'Market');
  const { labelLookup } = getLabelLookups(sections);
  const chosenCategories = getChosenCategories(values, labelLookup);

  const filterEventParams: EventParamType = {
    globalFilterApplied: 'Yes',
  };

  applyCategoryCombinationFilter(chosenCategories, filterEventParams);
  applyMarketFilters(marketFilters, chosenCategories, filterEventParams);

  return filterEventParams;
};

const applyCategoryCombinationFilter = (chosenCategories: ChosenCategoryType[], filterEventParams: EventParamType) => {
  const categoryCombination = chosenCategories.map(category => category.label);

  filterEventParams['categoryCombination'] = categoryCombination;
};

const applyMarketFilters = (
  marketFilters: FilterSections,
  chosenCategories: ChosenCategoryType[],
  filterEventParams: EventParamType
) => {
  if (!marketFilters) {
    return;
  }

  chosenCategories.forEach(category => {
    const { label: categoryChosen, values: optionChosen } = category;
    marketFilters.ungrouped.definitions.forEach(definition => {
      if (definition.label === categoryChosen) {
        filterEventParams[`${createLowerCamelCase(categoryChosen)}Applied`] = optionChosen;
      }
    });
  });
};

export const handlePageElementViewed = (
  context: Context,
  data: DataStore,
  componentSettings: Params,
  metadata: SettingsProvider['metadata']
) => {
  const dataViz = componentSettings?.dataViz?.[0] as DataVizType;

  const eventParams: EventParams = {};

  const chartType = mapDataVizForChartType(dataViz);
  if (chartType) {
    eventParams.chartType = chartType;
    eventParams.elementType = mapDataVizForElementType(dataViz);
  }

  const topSelectors = getTopSelectors(context.settings.values, context.settings.definitions);
  const appliedFilters = getAppliedFilters(context.filters);

  const metadataTrackingParams = getMetadataTrackingParams(metadata, componentSettings, data);

  const dataAvailability = getDataAvailability(data);

  const params = {
    ...dataAvailability,
    ...topSelectors,
    ...appliedFilters,
    ...eventParams,
    ...metadataTrackingParams,
  };

  return params;
};

export const useTrackPageElementViewed = (title: string | DynamicTitle) => {
  const context = ContextManager.getInstance().get();
  const { trackPageEvent } = useAnalyticsContext();

  const trackPageElementViewed = useCallback(
    (data: DataStore, componentSettings: Params, metadata: SettingsProvider['metadata']) => {
      const params = handlePageElementViewed(context, data, componentSettings, metadata);
      const eventParams = getComponentSettingsTrackingNames(componentSettings, title, params);
      trackPageEvent?.(EventName.PageElementViewed, eventParams);
    },
    [context, trackPageEvent, title]
  );

  return { trackPageElementViewed };
};

export const getComponentSettingsTrackingNames = (
  componentSettings: Params,
  title: string | DynamicTitle,
  eventParams: EventParams
): EventParams => {
  if (!title || typeof title === 'string') {
    return eventParams;
  }
  Object.keys(componentSettings).forEach(componentSettingKey => {
    const dynamicTitleMenuItems = title.filter(titleItem => typeof titleItem !== 'string');

    dynamicTitleMenuItems.forEach((title: TitleSelector) => {
      const { menu } = title;
      const trackingFilterName = menu.metadata?.tracking?.filterName;
      if (menu.paramName === componentSettingKey && trackingFilterName && !eventParams[trackingFilterName]) {
        //find corresponding component settings key from menu items
        const labels = getComponentSettingsValuesLabels(componentSettings, componentSettingKey, menu);
        eventParams = {
          ...eventParams,
          [trackingFilterName]: labels.join(', '),
        };
      }
    });
  });
  return eventParams;
};

const getComponentSettingsValuesLabels = (
  componentSettings: Params,
  componentSettingsKey: string,
  menu: SelectionMenu
): string[] => {
  const labels = componentSettings[componentSettingsKey].map(setting => {
    let menuItem;
    menu.items.forEach((item: ListMenuItem) => {
      if (!item) {
        return;
      }
      if (item.type === 'MenuItem' && Array.isArray(item.value)) {
        const match = getMenuItem(item.value as ListBoxMenuItem[], setting);
        if (match) {
          menuItem = match;
        }
      } else if (item.type === 'MenuItem' && (item.value as string) === setting) {
        menuItem = item;
      } else if (item.type === 'MenuItemGrouped') {
        const match = getMenuItem(item.list as ListBoxMenuItem[], setting);
        if (match) {
          menuItem = match;
        }
      }
    });
    return isBrandSegment(setting) ? 'Brand' : menuItem?.label || setting;
  });

  return labels;
};

const isBrandSegment = (segment: string) => segment === 'brand_name';

const getMenuItem = (items: ListBoxMenuItem[], setting: string) => {
  return items.find(item => item.value === setting);
};

export const getMetadataTrackingParams = (
  metadata: SettingsProvider['metadata'],
  componentSettings: Params,
  data: DataStore
) => {
  const trackingMetadata = metadata?.tracking;
  if (!trackingMetadata) {
    return {};
  }
  // set elementType, elementName, chartType only if they have a value
  const { paramsMap = [], elementType, elementName, chartType } = trackingMetadata;

  const trackingParams = {
    ...(elementType && { elementType }),
    ...(elementName && { elementName }),
    ...(chartType && { chartType }),
  };

  // paramMap example: {plColumns: "elementKpi", plFeatures:"features"}
  Object.keys(paramsMap).forEach(key => {
    const trackingPropertyName = paramsMap[key];
    trackingParams[trackingPropertyName] = componentSettings[key]?.map(
      (value: string) => data.getDataColumn(value)?.label
    );
  });

  return { ...trackingParams };
};

export const getDataAvailability = (data: DataStore) => {
  if (!data.hasDataResultSetsValues()) {
    return getDataAvailabilityParams(DataAvailability.No);
  }

  const hasData = data.hasDataInSomeGroupedSets();
  const dataHaveWarnings = data.hasWarnings();

  if (hasData && dataHaveWarnings) {
    return getDataAvailabilityParams(DataAvailability.Partial);
  }

  return getDataAvailabilityParams(DataAvailability.Full);
};

const getDataAvailabilityParams = (dataAvailability: DataAvailability) => ({ dataAvailability });
